import { FC, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import idmClasses from "../../../idm/ui/idm/idmWidget.module.scss";
import classes from "./staffTutorsWidget.module.scss";
import { tutorService } from "../../../../entities/tutor";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { FEATURES, hasFeature, useMyActions } from "../../../../entities/my";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { useAppSelector } from "../../../../app/store/store";
import { IBusinessLabel } from '../../../../shared/model/labels';
import { LabelBadges } from '../../../../shared/ui/labels';

interface IProps {
    isArchive: boolean;
    workTgIds: number[];
    withoutTg: boolean;
    labels: string[];
    isLowLoad: boolean;
    tagIds: number[];
    getStaffPath: (userId: number | null) => string;
    onCountChange?: (count: number) => void;
    searchValue?: string;
}

interface ITutorStaffListItem {
    user_id: number;
    full_name: string;
    email: string;
    roles: string[];
    name: string;
    labels: IBusinessLabel[];
    desired_hours: string;
    actual_hours: string;
}

export const StaffTutorsWidget: FC<IProps> = ({isArchive, workTgIds, withoutTg, labels, isLowLoad, tagIds, getStaffPath, onCountChange, searchValue = ''}) => {
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [items, setItems] = useState<ITutorStaffListItem[]>([])

    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const {my} = useAppSelector(state => state.myReducer)
    const workTgIdsKey = workTgIds.join(',')
    const workTgIdsFilter = useMemo(
        () => workTgIdsKey
            .split(',')
            .map(Number)
            .filter(id => !Number.isNaN(id) && id > 0),
        [workTgIdsKey]
    )

    const labelsKey = labels.join('\u0000')
    const tagIdsKey = tagIds.join(',')
    useEffect(() => {
        const getData = async () => {
            try{
                setIsLoading(true)
                const response = await tutorService.getAllByFilters(workTgIdsFilter, false, isArchive, withoutTg, labels, isLowLoad, tagIds)

                const normalizedItems = response.tutors
                    .map((tutor) => {
                        const tutorId = typeof tutor.user_id === 'number' && tutor.user_id > 0
                            ? tutor.user_id
                            : tutor.id

                        return {
                            user_id: tutorId,
                            full_name: tutor.full_name,
                            email: tutor.email || '',
                            roles: ['Исполнитель'],
                            name: `${tutor.full_name} ${tutorId} ${(tutor.labels || []).map(label => label.text).join(' ')}`,
                            labels: tutor.labels || [],
                            desired_hours: tutor.desired_hours || '',
                            actual_hours: tutor.actual_hours || '',
                        }
                    })
                    .filter(tutor => tutor.user_id > 0)

                setItems(normalizedItems)
                onCountChange?.(normalizedItems.length)
            }
            catch(e){
                onCountChange?.(0)
                console.log(e)
                if(e instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else if(e instanceof MyError){
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при получении списка исполнителей', type: 'error'})
                }
            }
            finally{
                setIsLoading(false)
            }
        }

        getData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isArchive, isLowLoad, withoutTg, workTgIdsKey, labelsKey, tagIdsKey])

    const emptyText = useMemo(() => isArchive ? 'Архив исполнителей пуст' : 'Список исполнителей пуст', [isArchive])
    const filteredEmptyText = useMemo(() => {
        if(searchValue.trim() || workTgIdsFilter.length > 0 || labels.length > 0 || withoutTg || isLowLoad || tagIds.length > 0){
            return 'Исполнители по выбранным фильтрам не найдены'
        }

        return emptyText
    }, [emptyText, isLowLoad, labels.length, searchValue, withoutTg, workTgIdsFilter.length, tagIds.length])
    const filteredItems = useMemo(() => {
        const normalizedSearchValue = searchValue.trim().toLowerCase()
        if(!normalizedSearchValue){
            return items
        }
        return items.filter(item => item.name.toLowerCase().includes(normalizedSearchValue))
    }, [items, searchValue])

    return (
        <section className={classes.container}>
            {
                isLoading
                    ?
                <section className={idmClasses.loader}><LoaderSpinner /></section>
                    : filteredItems.length === 0
                ? <section className={classes.empty}>{filteredEmptyText}</section>
                : <section className={classes.list}>
                    {filteredItems.map(item => {
                        const canOpenStaff = hasFeature(my, FEATURES.IDM.STAFF_READ, item.user_id)
                        const content = <>
                            {
                                <LabelBadges labels={item.labels} />
                            }
                            <span className={idmClasses.staffName}>{item.user_id}. {item.full_name}</span>
                            <span className={idmClasses.staffEmail}>{item.email || ' '}</span>
                            <span className={idmClasses.staffRoles}>План/факт: {item.desired_hours || '—'}/{item.actual_hours || '0'}</span>
                            <span className={idmClasses.staffRoles}>{item.roles.join(', ')}</span>
                        </>

                        return canOpenStaff
                            ? <Link
                                key={item.user_id}
                                to={getStaffPath(item.user_id)}
                                className={`${idmClasses.staffItem} staffLinkOverride`}
                            >
                                {content}
                            </Link>
                            : <section key={item.user_id} className={idmClasses.staffItem}>
                                {content}
                            </section>
                    })}
                </section>
            }
        </section>
    )
}
